import React from "react";
import { useReducer, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/primitives";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { PasswordForm } from "./components/PasswordForm";
import { getRandomBackground } from "./constants";
import { passwordReducer, createInitialState } from "./passwordReducer";

// Extract tokens from URL hash
function extractTokensFromHash(): {
	accessToken: string | null;
	refreshToken: string | null;
} {
	try {
		const hash = window.location.hash.replace(/^#/, "");
		const hashParams = new URLSearchParams(hash);
		return {
			accessToken: hashParams.get("access_token"),
			refreshToken: hashParams.get("refresh_token"),
		};
	} catch (e) {
		console.error("Error parsing URL hash:", e);
		return { accessToken: null, refreshToken: null };
	}
}

export function SetPasswordPage() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [state, dispatch] = useReducer(
		passwordReducer,
		createInitialState("set"),
	);

	useEffect(() => {
		dispatch({ type: "SET_BACKGROUND", payload: getRandomBackground() });
	}, []);

	// Check for expired/invalid link errors in both query params and hash
	useEffect(() => {
		try {
			// Check query params
			const sp = new URLSearchParams(window.location.search);
			const redirectParam = sp.get("redirect");
			let err = sp.get("error_code") || sp.get("error") || "";
			if (redirectParam) {
				const u = new URL(redirectParam, window.location.origin);
				const inner = u.searchParams;
				err = inner.get("error_code") || inner.get("error") || err;
			}

			// Also check hash fragment for errors
			const hash = window.location.hash.replace(/^#/, "");
			const hashParams = new URLSearchParams(hash);
			const hashError =
				hashParams.get("error_code") || hashParams.get("error") || "";
			err = err || hashError;

			if (err) {
				console.log("SetPasswordPage: link error detected:", err);
				if (err.includes("otp_expired")) {
					dispatch({
						type: "SET_LINK_ERROR",
						payload:
							"This invitation link has expired. Please contact your administrator for a new invitation.",
					});
				} else if (err.includes("email_link_invalid")) {
					dispatch({
						type: "SET_LINK_ERROR",
						payload:
							"This invitation link is invalid. Please contact your administrator for a new invitation.",
					});
				} else if (err.includes("access_denied")) {
					dispatch({
						type: "SET_LINK_ERROR",
						payload:
							"Access denied. Please contact your administrator for a new invitation.",
					});
				} else {
					dispatch({
						type: "SET_LINK_ERROR",
						payload:
							"There was an error with your invitation link. Please contact your administrator for a new invitation.",
					});
				}
			}
		} catch (e) {
			console.error("Error checking for link errors:", e);
		}
	}, []);

	const handleSetNewPassword = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (state.newPassword.length < 8) {
			dispatch({
				type: "SET_PASSWORD_ERROR",
				payload: "Password must be at least 8 characters long.",
			});
			return;
		}

		if (state.newPassword !== state.confirmNewPassword) {
			dispatch({
				type: "SET_PASSWORD_ERROR",
				payload: "Passwords do not match.",
			});
			return;
		}

		dispatch({ type: "SET_PASSWORD_START" });
		try {
			console.log("SetPasswordPage: setting password via edge function...");

			// Extract tokens from URL hash
			const { accessToken, refreshToken } = extractTokensFromHash();

			if (!accessToken || !refreshToken) {
				dispatch({
					type: "SET_PASSWORD_ERROR",
					payload: "Missing access token. Please use the invitation link.",
				});
				return;
			}

			// Call the edge function using supabase invoke
			const { data, error } = await supabase.functions.invoke("set-password", {
				body: {
					password: state.newPassword,
				},
				headers: {
					Authorization: `Bearer ${accessToken}`,
				},
			});

			if (error) {
				console.error("SetPasswordPage: set-password function error", error);
				dispatch({
					type: "SET_PASSWORD_ERROR",
					payload: error.message || "Failed to set password.",
				});
			} else if (data?.error) {
				dispatch({
					type: "SET_PASSWORD_ERROR",
					payload: data.details || data.error || "Failed to set password.",
				});
			} else {
				// Extract user_type from the response
				const userType = data?.user?.user_type as
					| "mentor"
					| "mentee"
					| undefined;

				// Determine the next page based on user type
				const nextPage = userType === "mentor" ? "/mentor-login" : "/login";

				// Update URL with next_page parameter using React Router and clear hash
				const newSearchParams = new URLSearchParams();
				newSearchParams.set("next_page", nextPage);
				navigate(
					{
						pathname: window.location.pathname,
						search: newSearchParams.toString(),
					},
					{ replace: true },
				);

				dispatch({ type: "SET_PASSWORD_SUCCESS", payload: userType });
			}
		} catch (err: any) {
			console.error("SetPasswordPage: Unexpected error:", err);
			dispatch({
				type: "SET_PASSWORD_ERROR",
				payload: err.message || "An unexpected error occurred.",
			});
		}
	};
	return (
		<div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
			{/* Left Panel - Background */}
			<div
				className="hidden lg:flex lg:w-1/2 items-center justify-center p-12 relative bg-cover bg-center bg-no-repeat"
				style={{
					backgroundImage: `url('${state.backgroundImage}')`,
				}}
			>
				<div className="absolute inset-0 bg-black/20"></div>
			</div>

			{/* Right Panel - Forms */}
			<div className="w-full lg:w-1/2 flex flex-col justify-between p-8">
				<div className="flex-1 flex items-center justify-center">
					<div className="w-full max-w-md">
						{state.view === "success" ? (
							/* Password Set Success */
							<Card>
								<CardHeader>
									<CardTitle className="flex items-center gap-2">
										<div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
											<CheckCircle2 className="w-5 h-5 text-green-600" />
										</div>
										Password set successfully
									</CardTitle>
									<CardDescription>
										Your password has been set. You can now login to your
										account.
									</CardDescription>
								</CardHeader>
								<CardContent>
									<Link to={searchParams.get("next_page") || "/"}>
										<Button className="w-full">Go to the login page</Button>
									</Link>
								</CardContent>
							</Card>
						) : (
							/* Set Password Form (view === "set") */
							<Card>
								<CardHeader>
									<CardTitle>Set your password</CardTitle>
									<CardDescription>
										Create a password for your account to get started.
									</CardDescription>
								</CardHeader>
								<CardContent>
									{state.linkError && (
										<div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
											<p className="text-sm text-red-800 dark:text-red-200 font-medium mb-2">
												Link Error
											</p>
											<p className="text-sm text-red-700 dark:text-red-300">
												{state.linkError}
											</p>
										</div>
									)}
									<PasswordForm
										newPassword={state.newPassword}
										confirmNewPassword={state.confirmNewPassword}
										showNewPassword={state.showNewPassword}
										showConfirmPassword={state.showConfirmPassword}
										isLoading={state.setPwLoading}
										error={state.setPwError}
										onPasswordChange={(value) =>
											dispatch({ type: "SET_NEW_PASSWORD", payload: value })
										}
										onConfirmPasswordChange={(value) =>
											dispatch({ type: "SET_CONFIRM_PASSWORD", payload: value })
										}
										onToggleShowPassword={() =>
											dispatch({ type: "TOGGLE_SHOW_NEW_PASSWORD" })
										}
										onToggleShowConfirmPassword={() =>
											dispatch({ type: "TOGGLE_SHOW_CONFIRM_PASSWORD" })
										}
										onSubmit={handleSetNewPassword}
										onCancel={() => navigate("/login")}
										passwordLabel="Password"
										confirmPasswordLabel="Confirm password"
										submitButtonText="Set password"
										cancelButtonText="Cancel"
									/>
								</CardContent>
							</Card>
						)}
					</div>
				</div>

				{/* Footer */}
				<div className="flex flex-col items-center justify-center mt-8 pt-4">
					<img
						src="/img/clarity-insights-logo.png"
						alt="Clarity Insights Logo"
						className="h-12 mb-2 opacity-60"
					/>
					<p className="text-xs text-gray-500 dark:text-gray-400">
						Copyright{" "}
						<a href="https://www.clarityinsightsgroup.com">
							Clarity Insights Group Ltd
						</a>{" "}
						2025
					</p>
				</div>
			</div>
		</div>
	);
}
